// Pure math for the visualizer styles — no three.js, no React, unit-testable
// by importing in-page and asserting on returned numbers.

/**
 * Unit direction `i` of `n` on a Fibonacci sphere — near-perfectly even
 * coverage, and y descends monotonically with i (index 0 = north pole), so
 * the drone index doubles as a latitude parameter.
 */
export function fibDir(i: number, n: number): [number, number, number] {
  const y = 1 - (2 * (i + 0.5)) / n;
  const r = Math.sqrt(Math.max(0, 1 - y * y));
  const a = i * 2.399963229728653; // the golden angle
  return [Math.cos(a) * r, y, Math.sin(a) * r];
}

/** Deterministic integer-lattice hash → [0, 1). */
function hash3(x: number, y: number, z: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(z, 1274126177)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Cheap 3D value noise in [-1, 1]: trilinear blend of lattice hashes with a
 * smoothstep fade. Deterministic, continuous, fast enough for ~20k calls a
 * frame — the nebula breathes on three phase-shifted copies of this.
 */
export function valueNoise3(x: number, y: number, z: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const sm = (t: number) => t * t * (3 - 2 * t);
  const fx = sm(x - xi), fy = sm(y - yi), fz = sm(z - zi);
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const v00 = lerp(hash3(xi, yi, zi), hash3(xi + 1, yi, zi), fx);
  const v10 = lerp(hash3(xi, yi + 1, zi), hash3(xi + 1, yi + 1, zi), fx);
  const v01 = lerp(hash3(xi, yi, zi + 1), hash3(xi + 1, yi, zi + 1), fx);
  const v11 = lerp(hash3(xi, yi + 1, zi + 1), hash3(xi + 1, yi + 1, zi + 1), fx);
  return lerp(lerp(v00, v10, fy), lerp(v01, v11, fy), fz) * 2 - 1;
}
